import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * VoiceRecorder — records mic audio and sends to /voice/stt.
 * onTranscript(text) is called with the resulting string.
 */
export default function VoiceRecorder({ onTranscript, testIdPrefix = "voice" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size === 0) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await api.post("/voice/stt", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data && data.text) onTranscript(data.text);
          else toast.error("Could not hear anything. Please try again.");
        } catch (err) {
          toast.error("Voice input is not available yet.");
        } finally {
          setBusy(false);
        }
      };
      mediaRecRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission was not given.");
    }
  };

  const stop = () => {
    const mr = mediaRecRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={busy}
      className={`tm-btn ${recording ? "tm-btn-primary" : "tm-btn-outline"}`}
      data-testid={`${testIdPrefix}-mic-btn`}
      aria-pressed={recording}
      aria-label={recording ? "Stop recording" : "Start voice input"}
    >
      {busy ? <Loader2 size={18} className="animate-spin" /> : recording ? <Square size={18} /> : <Mic size={18} />}
      <span className="hidden sm:inline">{busy ? "Listening…" : recording ? "Stop" : "Speak"}</span>
    </button>
  );
}
